import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";
import { useDropzone } from "react-dropzone";
import BulkUploadColumnGuide from "components/Common/BulkUploadColumnGuide";
import { getBulkTemplateUrl } from "apis/componentsAction/pages";

const dropAreaStyle = {
  padding: "30px",
  border: "2px dashed #ccc",
  borderRadius: "10px",
  cursor: "pointer",
  marginBottom: "20px",
};

const sampleBtn = {
  padding: "8px 16px",
  background: "#ffffff",
  color: "#0077E7",
  borderRadius: "20px",
  border: "1px solid #0077E7",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: 600,
  textDecoration: "none",
  display: "inline-block",
};

const resetBtn = {
  padding: "6px 14px",
  background: "#f5f5f5",
  color: "#333",
  border: "1px solid #ccc",
  borderRadius: "15px",
  cursor: "pointer",
  fontSize: "13px",
};

const defaultAccept = {
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "application/vnd.ms-excel": [".xls"],
};

function getAcceptExtensions(accept) {
  return Object.values(accept || {}).flat();
}

function getAcceptedFileTypeText(accept) {
  const extensions = getAcceptExtensions(accept);

  if (extensions.length === 1 && extensions[0] === ".csv") {
    return "CSV extension file only (max size 2mb)";
  }

  return "Excel extension file only (max size 2mb)";
}

const PLATFORM_API_MAP = {
  Facebook: "facebook",
  Instagram: "instagram",
  YouTube: "youtube",
  Twitter: "twitter",
};

function getSampleInfo(platform, sampleFileUrl, reportUrl) {
  if (sampleFileUrl) return { href: sampleFileUrl, base: "sample" };
  if (platform === "Report") return { href: reportUrl, base: "metadata" };
  const apiPlatform = PLATFORM_API_MAP[platform] || "facebook";
  return { href: getBulkTemplateUrl(apiPlatform), base: platform.toLowerCase() };
}

export default function BulkUploadDropzone({
  platform,
  onChange,
  onError,
  accept = defaultAccept,
  maxSize = 2e6,
  file_url = "",
  sampleFileUrl,
  sampleDownloadName,
  sampleLabel,
  showGuide = true,
}) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const acceptedExtensions = getAcceptExtensions(accept);
  const acceptedFileTypeText = getAcceptedFileTypeText(accept);
  const { href, base } = getSampleInfo(platform, sampleFileUrl, file_url);
  const downloadName =
    sampleDownloadName ||
    (sampleFileUrl
      ? undefined
      : `bulk_${String(platform || "facebook").toLowerCase()}_template.xlsx`);
  const buttonLabel = sampleLabel || (platform === "Report" ? "Download Current Mapping" : "Download Sample Sheet");

  const { getRootProps } = useDropzone({
    maxSize,
    maxFiles: 1,
    multiple: false,
    accept,
    onDragEnter: () => setDragActive(true),
    onDragLeave: () => setDragActive(false),
    onDropAccepted: (files) => {
      setDragActive(false);
      const f = files[0];
      setFile(f);
      onChange?.(f);
    },
    onDropRejected: (rejections) => {
      setDragActive(false);
      const r = rejections?.[0];
      const err = r?.errors?.[0];
      const msg = err
        ? `${err.code.split("-").join(" ")}: ${err.message}`
        : "File rejected";
      onError?.(msg, "error");
    },
  });

  return (
    <Box>
      <div
        {...getRootProps()}
        style={{
          ...dropAreaStyle,
          borderColor: dragActive ? "#1976d2" : "#ccc",
        }}
        onClick={() => document.getElementById("bulk-upload-input")?.click()}
      >
        {file ? (
          <Box sx={{ textAlign: "center" }}>
            <Typography sx={{ color: "#4F4F4F", fontWeight: 400, fontSize: "16px" }}>
              File Uploaded
            </Typography>
            <Typography sx={{ color: "#0077E7", fontWeight: 700, fontSize: "14px", mt: 0.5 }}>
              {file.name}
            </Typography>
            <button
              type="button"
              style={{ ...resetBtn, marginTop: "12px" }}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                onChange?.(null);
              }}
            >
              Reset
            </button>
          </Box>
        ) : (
          <>
            <DriveFolderUploadIcon style={{ fontSize: "60px", color: "#1976d2" }} />
            <p style={{ margin: "8px 0 0 0", fontWeight: 500, color: "#333" }}>
              Drag & Drop File Here
            </p>
            <p style={{ margin: "4px 0 0 0", color: "#666", fontSize: "14px" }}>
              & Click to Upload
            </p>
            <p style={{ margin: "16px 0 0 0", color: "#787575", fontSize: "13px" }}>
              {acceptedFileTypeText}
            </p>
          </>
        )}
      </div>

      <input
        id="bulk-upload-input"
        type="file"
        accept={acceptedExtensions.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files[0];
          if (f) {
            setFile(f);
            onChange?.(f);
          }
        }}
      />

      <a
        download={downloadName}
        target="_blank"
        rel="noreferrer"
        href={href}
        style={{ textDecoration: "none" }}
      >
        <span style={sampleBtn}>{buttonLabel}</span>
      </a>

      {showGuide && platform !== "Report" && <BulkUploadColumnGuide platform={platform} />}
    </Box>
  );
}
